import { CloseOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Flex,
  Form,
  Modal,
  Select,
  Tooltip,
  Typography
} from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_BULK_BAR_CLASS } from "../../../../components/Table/table-bulk-bar.ts"
import type { ProjectBulkResult } from "../../redux/projects.types.ts"

const { Text } = Typography

export type ProjectBulkAction = "change-status" | "assign-pm" | "archive"

export interface ProjectBulkActionParams {
  action: ProjectBulkAction
  ids: string[]
  status?: number
  projectManagerId?: string
}

interface Option<V> {
  label: string
  value: V
}

interface ProjectsBulkActionBarProps {
  selectedIds: string[]
  // A selection survives loading more rows, so a failure can name a project
  // whose row is not currently mounted — fall back to the id when it is absent.
  projectNames: Record<string, string>
  statusOptions: Option<number>[]
  projectManagerOptions: Option<string>[]
  editAllowed?: boolean
  assignPmAllowed?: boolean
  archiveAllowed?: boolean
  onClear: () => void
  onAction: (params: ProjectBulkActionParams) => Promise<ProjectBulkResult>
  /**
   * The bar outlives the selection — it keeps reporting the outcome after an
   * action clears it — so anything anchored to the same corner follows this
   * rather than the selection.
   */
  onVisibleChange?: (visible: boolean) => void
}

type BulkFormValues = { status?: number; projectManagerId?: string }

export function ProjectsBulkResult({
  result,
  projectNames
}: {
  result: ProjectBulkResult
  projectNames: Record<string, string>
}) {
  const { t } = useTranslation()

  return (
    <div>
      <Text strong>
        {t("projectBulk.result.summary", {
          succeeded: result.succeeded.length,
          requested: result.requested
        })}
      </Text>
      {/* Bounded because the bar is pinned to the bottom: an unbounded list
          grows upwards over the table rather than overflowing down. */}
      {result.failed.length ? (
        <ul
          data-testid="projects-bulk-result-failures"
          // Nothing inside takes focus, so the region carries it — otherwise
          // the refusals below the cap are unreachable without a pointer.
          tabIndex={0}
          aria-label={t("projectBulk.result.failuresLabel", {
            count: result.failed.length
          })}
          className="mb-0 mt-2 max-h-[30vh] list-disc overflow-y-auto pl-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2A7D8C]"
        >
          {result.failed.map((failure) => (
            <li key={failure.id}>
              {t("projectBulk.result.failure", {
                project: projectNames[failure.id] || failure.id,
                reason: failure.message
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function ProjectsBulkActionBar({
  selectedIds,
  projectNames,
  statusOptions,
  projectManagerOptions,
  editAllowed,
  assignPmAllowed,
  archiveAllowed,
  onClear,
  onAction,
  onVisibleChange
}: ProjectsBulkActionBarProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<BulkFormValues>()
  const [activeAction, setActiveAction] = useState<ProjectBulkAction | null>(
    null
  )
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ProjectBulkResult | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)

  const actionLabels = useMemo<Record<ProjectBulkAction, string>>(
    () => ({
      "change-status": t("projectBulk.actions.changeStatus"),
      "assign-pm": t("projectBulk.actions.assignPm"),
      archive: t("projectBulk.actions.archive")
    }),
    [t]
  )

  const closeModal = () => {
    if (submitting) return
    setActiveAction(null)
    form.resetFields()
  }

  const openModal = (action: ProjectBulkAction) => {
    form.resetFields()
    setActiveAction(action)
  }

  const submitAction = async () => {
    if (!activeAction) return

    let values: BulkFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    // The field's own rule covers this, but the request must not depend on the
    // form being connected in time.
    if (activeAction === "change-status" && values.status == null) return
    if (activeAction === "assign-pm" && !values.projectManagerId) return

    setSubmitting(true)
    try {
      const nextResult = await onAction({
        action: activeAction,
        ids: selectedIds,
        ...(activeAction === "change-status" ? { status: values.status } : {}),
        ...(activeAction === "assign-pm"
          ? { projectManagerId: values.projectManagerId }
          : {})
      })
      setRequestFailed(false)
      setResult(nextResult)
      setActiveAction(null)
      form.resetFields()
    } catch {
      setResult(null)
      setRequestFailed(true)
      setActiveAction(null)
      form.resetFields()
    } finally {
      setSubmitting(false)
    }
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    onClear()
  }

  const visible = !!selectedIds.length || !!result || requestFailed

  useEffect(() => {
    onVisibleChange?.(visible)
  }, [visible, onVisibleChange])

  if (!visible) return null

  const buttonClass =
    "!h-9 !border-[#2A7D8C] !bg-[#2A7D8C] !text-white hover:!border-[#236b78] hover:!bg-[#236b78]"

  return (
    <div
      className={TABLE_BULK_BAR_CLASS}
      data-testid="projects-bulk-action-bar"
    >
      {selectedIds.length ? (
        <Flex
          align="center"
          gap="small"
          className="overflow-x-auto bg-[#17383E] px-4 py-3 text-white"
          role="toolbar"
          aria-label={t("projectBulk.toolbarLabel")}
          wrap={false}
        >
          <Text strong className="mr-2 whitespace-nowrap !text-white">
            {t("projectBulk.selected", { count: selectedIds.length })}
          </Text>

          {editAllowed ? (
            <Button
              className={buttonClass}
              onClick={() => openModal("change-status")}
            >
              {actionLabels["change-status"]}
            </Button>
          ) : null}

          {assignPmAllowed ? (
            <Button
              className={buttonClass}
              onClick={() => openModal("assign-pm")}
            >
              {actionLabels["assign-pm"]}
            </Button>
          ) : null}

          {archiveAllowed ? (
            <Button danger type="primary" onClick={() => openModal("archive")}>
              {actionLabels.archive}
            </Button>
          ) : null}

          <Tooltip title={t("projectBulk.clearSelection")}>
            <Button
              // The fixed support launcher expands from the right edge; the
              // trailing margin keeps this dismiss action clickable next to it.
              className="ml-auto mr-16 shrink-0 !text-white"
              type="text"
              icon={<CloseOutlined />}
              aria-label={t("projectBulk.clearSelection")}
              onClick={clear}
            />
          </Tooltip>
        </Flex>
      ) : null}

      {/* Below the toolbar, so a result does not push the controls still in
          use further down the page. */}
      {requestFailed ? (
        <Alert
          className="rounded-t-none"
          data-testid="projects-bulk-request-error"
          type="error"
          showIcon
          closable
          onClose={() => setRequestFailed(false)}
          message={t("projectBulk.result.requestFailed")}
        />
      ) : null}

      {result ? (
        <Alert
          className="rounded-t-none"
          type={result.failed.length ? "warning" : "success"}
          showIcon
          closable
          onClose={() => setResult(null)}
          message={
            <ProjectsBulkResult
              result={result}
              projectNames={projectNames}
            />
          }
        />
      ) : null}

      <Modal
        open={activeAction != null}
        title={activeAction ? actionLabels[activeAction] : undefined}
        okText={
          activeAction === "archive"
            ? t("projectBulk.actions.archive")
            : t("projectBulk.apply")
        }
        cancelText={t("projectBulk.cancel")}
        okButtonProps={{ danger: activeAction === "archive" }}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        keyboard={!submitting}
        maskClosable={false}
        destroyOnHidden
        onOk={submitAction}
        onCancel={closeModal}
      >
        <Form form={form} layout="vertical" disabled={submitting}>
          {activeAction === "change-status" ? (
            <Form.Item<BulkFormValues>
              name="status"
              label={t("projectBulk.fields.status")}
              rules={[
                {
                  required: true,
                  message: t("projectBulk.validation.status")
                }
              ]}
            >
              <Select
                options={statusOptions}
                placeholder={t("projectBulk.placeholders.status")}
              />
            </Form.Item>
          ) : null}

          {activeAction === "assign-pm" ? (
            <>
              <Form.Item<BulkFormValues>
                name="projectManagerId"
                label={t("projectBulk.fields.projectManager")}
                rules={[
                  {
                    required: true,
                    message: t("projectBulk.validation.projectManager")
                  }
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={projectManagerOptions}
                  placeholder={t("projectBulk.placeholders.projectManager")}
                />
              </Form.Item>
              <Text type="secondary">{t("projectBulk.assignPmHint")}</Text>
            </>
          ) : null}

          {activeAction === "archive" ? (
            <Alert
              type="error"
              showIcon
              message={t("projectBulk.archiveImpact", {
                count: selectedIds.length
              })}
            />
          ) : null}
        </Form>
      </Modal>
    </div>
  )
}
